import { useCallback } from 'react';
import { ClientForm, CommunicationMethod } from '../api/types/digitalForms.api.types';
import { usePatientAge } from '../../../hooks/usePatientAge';
import { usePatientSex } from '../../../hooks/usePatientSex';
import { useFormattedPhoneNumber } from '../../../hooks/useFormattedPhoneNumber';

export const usePopulateHtmlTemplate = () => {
  const { getPatientAge } = usePatientAge();
  const { getPatientSex } = usePatientSex();
  const { formatDecoratedPhoneNumber } = useFormattedPhoneNumber();

  const populateTemplate = useCallback(
    ({ template, clientForm }: { template: string; clientForm: ClientForm }) => {
      if (!clientForm) return template;

      const { patient, client } = clientForm;

      // Get patient age
      const patientAge = patient
        ? getPatientAge({
            birthday: patient.birthday,
            deathDate: patient.deathDate,
            wasEuthanized: patient.wasEuthanized,
          })
        : '';

      // Get patient sex
      const patientSex = patient ? getPatientSex({ sexId: patient.sexId }) : '';

      // Build signalment: {patient age} {patient sex} {patient breed}
      const signalmentParts = [patientAge, patientSex, patient?.breed].filter(Boolean);
      const patientSignalment = signalmentParts.join(' ');

      // Get primary phone number
      const primaryPhone = client?.communicationOptions?.find((option) => option.communicationMethodId === CommunicationMethod.Phone && option.isPrimary);
      const clientPhone = primaryPhone?.value ? formatDecoratedPhoneNumber(primaryPhone.value) : '';

      // Get primary email
      const primaryEmail = client?.communicationOptions?.find((option) => option.communicationMethodId === CommunicationMethod.Email && option.isPrimary);
      const clientEmail = primaryEmail?.value || '';

      // Build client address
      const addressParts = [client?.address1, client?.address2, client?.city, client?.state, client?.zipcode].filter(Boolean);
      const clientAddress = addressParts.join(', ');

      // Build client name - use client firstName/lastName if available, otherwise fall back to clientForm.clientName
      const clientNameParts = [client?.firstName, client?.lastName].filter(Boolean);
      const clientName = clientNameParts.length > 0 ? clientNameParts.join(' ') : clientForm.clientName || '';

      // Define replacement variables
      const replacements: Record<string, string> = {
        // Patient variables
        patientName: patient?.petName || '',
        patientAge: patientAge || '',
        patientSex: patientSex || '',
        patientBreed: patient?.breed || '',
        patientSpecies: patient?.species || '',
        patientColor: patient?.color || '',
        patientSignalment: patientSignalment,
        microchipNumber: patient?.microchipId || '',
        patientId: patient?.localId || '',

        // Client variables
        clientName: clientName,
        clientFirstName: client?.firstName || '',
        clientLastName: client?.lastName || '',
        clientPhone: clientPhone,
        clientEmail: clientEmail,
        clientAddress: clientAddress,
        clientAddress1: client?.address1 || '',
        clientAddress2: client?.address2 || '',
        clientCity: client?.city || '',
        clientState: client?.state || '',
        clientZipcode: client?.zipcode || '',

        // Form variables
        accountId: client?.localId || '',
        formId: clientForm.id.toString(),
        hospitalId: clientForm.hospitalId.toString(),
      };

      // Replace mustache variables with actual values
      let populatedTemplate = template;

      Object.entries(replacements).forEach(([key, value]) => {
        const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'g');
        populatedTemplate = populatedTemplate.replace(regex, value);
      });

      return populatedTemplate;
    },
    [getPatientAge, getPatientSex, formatDecoratedPhoneNumber]
  );

  return { populateTemplate };
};
